// miniapp/src/components/admin/CannedAdminScreen.tsx
//
// Phase 37 (Canned Responses) Plan 05 — the level>=2 admin CRUD screen.
//
// Entry point (LOCKED, 37-CONTEXT.md "Frontend"): opened by tapping the
// agent name/level chip in TopNavbar — NOT a generic dashboard button. App.tsx
// renders this as the 'cannedAdmin' AppView and passes `onBack`.
//
// What it does:
//   - PREVIEWS the existing answer library (ALL answers incl. inactive, grouped
//     by category, with an Active/Inactive badge) — the "library preview" the
//     LOCKED Frontend decision requires.
//   - Category CRUD: create / edit (name + sortOrder) / delete. Deleting a
//     non-empty category surfaces the backend 409 ("Category still has answers")
//     inline instead of crashing.
//   - Answer CRUD: create / edit (category + title + body + sortOrder) +
//     soft-delete via an Active toggle (Deactivate / Reactivate). There is NO
//     hard-delete for answers (soft-delete is the removal path per 37-CONTEXT).
//     A duplicate (category, title) on create/edit surfaces the backend 409
//     ("An answer with this title already exists…") inline.
//
// `name` / `title` / `body` are free-text fields (any language).
//
// The frontend level>=2 hide (in App.tsx / TopNavbar) is UX only — the real
// enforcement is the server-side requireLevel(2) gate shipped + tested in 37-03.
//
// Data layer: everything is driven by the useCannedResponses hook (37-04). Each
// mutation returns { ok: true } | { ok: false, error } and the hook re-fetches
// the affected admin list, so the lists re-render after a successful save.
//
// (37-07 adds a CSV bulk-import panel; a placeholder slot is left in the header
// actions / Answers section header — see the `csv-import-slot` comment below.)

import { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  X,
  Loader2,
} from 'lucide-react';
import { useCannedResponses } from '../../hooks/useCannedResponses';
import { CannedResponseImport } from './CannedResponseImport';
import type { CannedAnswer, CannedCategory, CaseType } from '../../types';

// ── Phase 38: case-type selector options (SV / WL / General) ────────────────
// The REAL uppercase Phase 36 caseType values ('SV' = SIM Verifier, 'WL' =
// Wallet) + General = null. These are the ONLY three choices; the picker (38-03)
// matches a category's caseType against the ticket's caseType by equality.
const CASE_TYPE_OPTIONS: { value: CaseType | null; label: string }[] = [
  { value: null, label: 'General' },
  { value: 'SV', label: 'SV (SIM Verifier)' },
  { value: 'WL', label: 'WL (Wallet)' },
];

/** Short pill label for a category row badge. null → "General". */
function caseTypeBadge(caseType: CaseType | null | undefined): {
  label: string;
  muted: boolean;
} {
  if (caseType === 'SV') return { label: 'SV', muted: false };
  if (caseType === 'WL') return { label: 'WL', muted: false };
  return { label: 'General', muted: true };
}

interface CannedAdminScreenProps {
  onBack: () => void;
}

// ── Local form state shapes ──────────────────────────────────────────────
interface CategoryForm {
  /** null = creating; a category = editing that row. */
  editing: CannedCategory | null;
  name: string;
  sortOrder: string; // kept as string for a controlled <input>; parsed on submit
  /**
   * Phase 38: the chosen case-type tag — 'SV' | 'WL' | null (General). Default
   * null for a new category; pre-filled from the row's caseType on edit.
   */
  caseType: CaseType | null;
}

interface AnswerForm {
  /** null = creating; an answer = editing that row. */
  editing: CannedAnswer | null;
  categoryId: number | '';
  title: string;
  body: string;
  sortOrder: string;
}

export function CannedAdminScreen({ onBack }: CannedAdminScreenProps) {
  const {
    adminCategories,
    adminAnswers,
    isAdminLoading,
    adminError,
    loadAdminData,
    createCategory,
    updateCategory,
    deleteCategory,
    createAnswer,
    updateAnswer,
    setAnswerActive,
    deleteAnswer,
  } = useCannedResponses();

  // Modal forms (null = closed).
  const [categoryForm, setCategoryForm] = useState<CategoryForm | null>(null);
  const [answerForm, setAnswerForm] = useState<AnswerForm | null>(null);
  // The answer pending a hard-delete confirm (null = no dialog). Hard-delete is
  // permanent, so it ALWAYS confirms first.
  const [deleteConfirm, setDeleteConfirm] = useState<CannedAnswer | null>(null);

  // Inline action errors keyed by surface so a category-delete 409 / a
  // duplicate-title 409 renders next to the thing that failed (no crash).
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [answerError, setAnswerError] = useState<string | null>(null);
  // Per-form (modal) submit error + a "saving" guard.
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Id of the row currently toggling active / being deleted (for a busy spinner).
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  // Map categoryId → name for labeling answers grouped under their category.
  const categoryName = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of adminCategories) m.set(c.id, c.name);
    return m;
  }, [adminCategories]);

  // Group ALL answers (incl. inactive) by category for the library preview.
  const answersByCategory = useMemo(() => {
    const groups = new Map<number, CannedAnswer[]>();
    for (const a of adminAnswers) {
      const arr = groups.get(a.categoryId) ?? [];
      arr.push(a);
      groups.set(a.categoryId, arr);
    }
    for (const arr of groups.values()) {
      arr.sort((x, y) => x.sortOrder - y.sortOrder || x.id - y.id);
    }
    return groups;
  }, [adminAnswers]);

  const sortedCategories = useMemo(
    () => [...adminCategories].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id),
    [adminCategories],
  );

  // ── Category actions ─────────────────────────────────────────────────────
  const openCreateCategory = () => {
    setFormError(null);
    // Phase 38: new categories default to General (caseType null).
    setCategoryForm({ editing: null, name: '', sortOrder: '', caseType: null });
  };

  const openEditCategory = (cat: CannedCategory) => {
    setFormError(null);
    // Phase 38: pre-select the row's current caseType (General when null).
    setCategoryForm({
      editing: cat,
      name: cat.name,
      sortOrder: String(cat.sortOrder),
      caseType: cat.caseType ?? null,
    });
  };

  const submitCategory = async () => {
    if (!categoryForm) return;
    const name = categoryForm.name.trim();
    if (!name) {
      setFormError('Name is required');
      return;
    }
    const sortOrder = parseSortOrder(categoryForm.sortOrder);
    if (sortOrder === 'invalid') {
      setFormError('Sort order must be a non-negative number');
      return;
    }
    setSaving(true);
    setFormError(null);
    // Phase 38: always include caseType so an edit can CLEAR a tag back to
    // General (null) — key presence drives the server SET. On create, null →
    // server defaults to General.
    const result = categoryForm.editing
      ? await updateCategory(categoryForm.editing.id, {
          name,
          ...(sortOrder !== undefined ? { sortOrder } : {}),
          caseType: categoryForm.caseType,
        })
      : await createCategory({
          name,
          ...(sortOrder !== undefined ? { sortOrder } : {}),
          caseType: categoryForm.caseType,
        });
    setSaving(false);
    if (result.ok) {
      setCategoryForm(null);
    } else {
      setFormError(result.error);
    }
  };

  const handleDeleteCategory = async (cat: CannedCategory) => {
    setCategoryError(null);
    setBusyId(cat.id);
    const result = await deleteCategory(cat.id);
    setBusyId(null);
    if (!result.ok) {
      // Backend 409 when the category still has answers — surface inline,
      // never crash. The wire copy is "Category has answers; deactivate them
      // or move them first" (37-03); show a friendly version.
      setCategoryError(
        /has answers|in[_ ]use|409/i.test(result.error)
          ? `Category still has answers — deactivate or move them first (${cat.name}).`
          : result.error,
      );
    }
  };

  // ── Answer actions ───────────────────────────────────────────────────────
  const openCreateAnswer = () => {
    setFormError(null);
    setAnswerForm({
      editing: null,
      categoryId: sortedCategories[0]?.id ?? '',
      title: '',
      body: '',
      sortOrder: '',
    });
  };

  const openEditAnswer = (ans: CannedAnswer) => {
    setFormError(null);
    setAnswerForm({
      editing: ans,
      categoryId: ans.categoryId,
      title: ans.title,
      body: ans.body,
      sortOrder: String(ans.sortOrder),
    });
  };

  const submitAnswer = async () => {
    if (!answerForm) return;
    if (answerForm.categoryId === '') {
      setFormError('Please pick a category');
      return;
    }
    const title = answerForm.title.trim();
    const body = answerForm.body.trim();
    if (!title) {
      setFormError('Title is required');
      return;
    }
    if (!body) {
      setFormError('Answer body is required');
      return;
    }
    const sortOrder = parseSortOrder(answerForm.sortOrder);
    if (sortOrder === 'invalid') {
      setFormError('Sort order must be a non-negative number');
      return;
    }
    setSaving(true);
    setFormError(null);
    const result = answerForm.editing
      ? await updateAnswer(answerForm.editing.id, {
          categoryId: answerForm.categoryId,
          title,
          body,
          ...(sortOrder !== undefined ? { sortOrder } : {}),
        })
      : await createAnswer({
          categoryId: answerForm.categoryId,
          title,
          body,
          ...(sortOrder !== undefined ? { sortOrder } : {}),
        });
    setSaving(false);
    if (result.ok) {
      setAnswerForm(null);
    } else {
      // Duplicate (category, title) → backend 409 "An answer with this title
      // already exists in this category". Surface inline in the form.
      setFormError(
        /already exists|duplicate|409/i.test(result.error)
          ? 'An answer with this title already exists in this category.'
          : result.error,
      );
    }
  };

  const handleToggleActive = async (ans: CannedAnswer) => {
    setAnswerError(null);
    setBusyId(ans.id);
    const result = await setAnswerActive(ans.id, !ans.isActive);
    setBusyId(null);
    if (!result.ok) setAnswerError(result.error);
  };

  // Hard-delete (permanent). Only runs after the confirm dialog is accepted.
  const handleConfirmDeleteAnswer = async () => {
    if (!deleteConfirm) return;
    const ans = deleteConfirm;
    setAnswerError(null);
    setBusyId(ans.id);
    const result = await deleteAnswer(ans.id);
    setBusyId(null);
    setDeleteConfirm(null);
    if (!result.ok) setAnswerError(result.error);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-full bg-surface-secondary">
      {/* Header row: Back + title + Add affordances */}
      <div className="sticky top-0 z-10 bg-white border-b border-border-primary px-4 sm:px-6 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-0.5 text-[#007AFF] -ml-1 touch-active"
            aria-label="Back"
          >
            <ChevronLeft className="h-6 w-6 flex-shrink-0" strokeWidth={2.5} aria-hidden />
            <span className="text-[17px] leading-none">Back</span>
          </button>
          <h1 className="flex-1 text-base font-semibold text-text-primary text-center">
            Canned Responses (Admin)
          </h1>
          {/* spacer to balance the Back button so the title centers */}
          <span className="w-[64px]" aria-hidden />
        </div>
        {/* csv-import-slot: 37-07 mounts the "Import CSV" / "Download template"
            actions here (a row under the title). Intentionally empty in 37-05. */}
      </div>

      {/* Top-level admin-load error (e.g. a stale UI hitting the 403 gate). */}
      {adminError && (
        <div className="mx-4 sm:mx-6 mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {adminError}
        </div>
      )}

      {isAdminLoading && adminCategories.length === 0 && adminAnswers.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-text-secondary">
          <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden />
          Loading…
        </div>
      ) : (
        <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-4 space-y-8">
          {/* ── Bulk import (CSV) — 37-07's csv-import-slot ──────────────── */}
          {/* Mounts the import panel. onImported re-loads THIS screen's lists
              (the panel holds its own hook instance) so the library preview
              reflects the new/updated answers + categories after a commit. */}
          <CannedResponseImport onImported={loadAdminData} />

          {/* ── Categories section ──────────────────────────────────────── */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
                Categories
              </h2>
              <button
                type="button"
                onClick={openCreateCategory}
                className="flex items-center gap-1 text-sm font-medium text-[#2563EB] touch-active"
              >
                <Plus className="h-4 w-4" aria-hidden /> Add category
              </button>
            </div>

            {categoryError && (
              <div className="mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                {categoryError}
              </div>
            )}

            {sortedCategories.length === 0 ? (
              <p className="text-sm text-text-secondary py-3">No categories yet.</p>
            ) : (
              <ul className="divide-y divide-border-primary rounded-xl border border-border-primary bg-white">
                {sortedCategories.map((cat) => (
                  <li key={cat.id} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-text-primary truncate">{cat.name}</p>
                        {/* Phase 38: case-type tag — SV/WL pill or a muted General. */}
                        {(() => {
                          const badge = caseTypeBadge(cat.caseType);
                          return (
                            <span
                              className={
                                badge.muted
                                  ? 'flex-shrink-0 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-gray-100 text-gray-500'
                                  : 'flex-shrink-0 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700'
                              }
                              aria-label={`Case type ${badge.label}`}
                            >
                              {badge.label}
                            </span>
                          );
                        })()}
                      </div>
                      <p className="text-xs text-text-secondary">Sort {cat.sortOrder}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openEditCategory(cat)}
                      className="p-2 rounded-lg text-text-secondary hover:bg-gray-100 touch-active"
                      aria-label={`Edit category ${cat.name}`}
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteCategory(cat)}
                      disabled={busyId === cat.id}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-50 touch-active disabled:opacity-50"
                      aria-label={`Delete category ${cat.name}`}
                    >
                      {busyId === cat.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <Trash2 className="h-4 w-4" aria-hidden />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ── Answers section (the library preview) ───────────────────── */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
                Answer Library
              </h2>
              <button
                type="button"
                onClick={openCreateAnswer}
                disabled={sortedCategories.length === 0}
                className="flex items-center gap-1 text-sm font-medium text-[#2563EB] touch-active disabled:opacity-40"
                title={
                  sortedCategories.length === 0 ? 'Add a category first' : undefined
                }
              >
                <Plus className="h-4 w-4" aria-hidden /> Add answer
              </button>
            </div>

            {answerError && (
              <div className="mb-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {answerError}
              </div>
            )}

            {adminAnswers.length === 0 ? (
              <p className="text-sm text-text-secondary py-3">
                No answers yet — the library is empty.
              </p>
            ) : (
              <div className="space-y-4">
                {sortedCategories.map((cat) => {
                  const answers = answersByCategory.get(cat.id) ?? [];
                  if (answers.length === 0) return null;
                  return (
                    <div key={cat.id}>
                      <p className="text-xs font-semibold text-text-secondary mb-1">
                        {cat.name}
                      </p>
                      <ul className="divide-y divide-border-primary rounded-xl border border-border-primary bg-white">
                        {answers.map((ans) => (
                          <AnswerRow
                            key={ans.id}
                            answer={ans}
                            busy={busyId === ans.id}
                            onEdit={() => openEditAnswer(ans)}
                            onToggleActive={() => handleToggleActive(ans)}
                            onDelete={() => setDeleteConfirm(ans)}
                          />
                        ))}
                      </ul>
                    </div>
                  );
                })}
                {/* Answers whose category is missing (shouldn't normally happen). */}
                {orphanAnswers(adminAnswers, categoryName).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-text-secondary mb-1">Uncategorized</p>
                    <ul className="divide-y divide-border-primary rounded-xl border border-border-primary bg-white">
                      {orphanAnswers(adminAnswers, categoryName).map((ans) => (
                        <AnswerRow
                          key={ans.id}
                          answer={ans}
                          busy={busyId === ans.id}
                          onEdit={() => openEditAnswer(ans)}
                          onToggleActive={() => handleToggleActive(ans)}
                          onDelete={() => setDeleteConfirm(ans)}
                        />
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ── Category modal ──────────────────────────────────────────────── */}
      {categoryForm && (
        <FormModal
          title={categoryForm.editing ? 'Edit category' : 'Add category'}
          onClose={() => setCategoryForm(null)}
          onSubmit={submitCategory}
          saving={saving}
          error={formError}
          submitLabel={categoryForm.editing ? 'Save' : 'Create'}
        >
          <Field label="Name">
            <input
              type="text"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              placeholder="Category name"
              className={inputClass}
              autoFocus
            />
          </Field>
          {/* Phase 38: case-type selector (SV / WL / General). Default General
              for a new category; pre-filled on edit; persists incl. clear-to-null. */}
          <Field label="Case type">
            <select
              value={categoryForm.caseType ?? ''}
              onChange={(e) =>
                setCategoryForm({
                  ...categoryForm,
                  caseType: e.target.value === '' ? null : (e.target.value as CaseType),
                })
              }
              className={inputClass}
            >
              {CASE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.value ?? ''}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sort order (optional)">
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={categoryForm.sortOrder}
              onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: e.target.value })}
              placeholder="0"
              className={inputClass}
            />
          </Field>
        </FormModal>
      )}

      {/* ── Answer modal ────────────────────────────────────────────────── */}
      {answerForm && (
        <FormModal
          title={answerForm.editing ? 'Edit answer' : 'Add answer'}
          onClose={() => setAnswerForm(null)}
          onSubmit={submitAnswer}
          saving={saving}
          error={formError}
          submitLabel={answerForm.editing ? 'Save' : 'Create'}
        >
          <Field label="Category">
            <select
              value={answerForm.categoryId}
              onChange={(e) =>
                setAnswerForm({
                  ...answerForm,
                  categoryId: e.target.value === '' ? '' : Number(e.target.value),
                })
              }
              className={inputClass}
            >
              {sortedCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Title">
            <input
              type="text"
              value={answerForm.title}
              onChange={(e) => setAnswerForm({ ...answerForm, title: e.target.value })}
              placeholder="Title"
              className={inputClass}
              autoFocus
            />
          </Field>
          <Field label="Answer body">
            <textarea
              value={answerForm.body}
              onChange={(e) => setAnswerForm({ ...answerForm, body: e.target.value })}
              placeholder="Answer text"
              rows={5}
              className={`${inputClass} resize-y`}
            />
          </Field>
          <Field label="Sort order (optional)">
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={answerForm.sortOrder}
              onChange={(e) => setAnswerForm({ ...answerForm, sortOrder: e.target.value })}
              placeholder="0"
              className={inputClass}
            />
          </Field>
        </FormModal>
      )}

      {/* ── Hard-delete confirm dialog ──────────────────────────────────── */}
      {deleteConfirm && (
        <ConfirmModal
          title="Delete answer?"
          message={`This permanently deletes "${deleteConfirm.title}". This cannot be undone. To hide an answer instead, use Deactivate.`}
          confirmLabel="Delete"
          destructive
          busy={busyId === deleteConfirm.id}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={handleConfirmDeleteAnswer}
        />
      )}
    </div>
  );
}

// ── Answer row (library preview entry) ──────────────────────────────────────
function AnswerRow({
  answer,
  busy,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  answer: CannedAnswer;
  busy: boolean;
  onEdit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="flex items-start gap-3 px-3 py-2.5">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-text-primary truncate">{answer.title}</p>
          <span
            className={
              answer.isActive
                ? 'flex-shrink-0 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-green-100 text-green-700'
                : 'flex-shrink-0 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-gray-200 text-gray-600'
            }
          >
            {answer.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <p className="text-xs text-text-secondary line-clamp-2 whitespace-pre-wrap break-words">
          {answer.body}
        </p>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="p-2 rounded-lg text-text-secondary hover:bg-gray-100 touch-active"
        aria-label={`Edit answer ${answer.title}`}
      >
        <Pencil className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onToggleActive}
        disabled={busy}
        className="p-2 rounded-lg text-text-secondary hover:bg-gray-100 touch-active disabled:opacity-50"
        aria-label={answer.isActive ? `Deactivate ${answer.title}` : `Reactivate ${answer.title}`}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : answer.isActive ? (
          <EyeOff className="h-4 w-4" aria-hidden />
        ) : (
          <Eye className="h-4 w-4" aria-hidden />
        )}
      </button>
      {/* Hard-delete (permanent) — opens a confirm dialog before removing. */}
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        className="p-2 rounded-lg text-red-500 hover:bg-red-50 touch-active disabled:opacity-50"
        aria-label={`Delete ${answer.title}`}
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>
    </li>
  );
}

// ── Shared modal shell (reuses CloseConfirmModal overlay+sheet tokens) ───────
function FormModal({
  title,
  onClose,
  onSubmit,
  saving,
  error,
  submitLabel,
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  saving: boolean;
  error: string | null;
  submitLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
      style={{ touchAction: 'none' }}
    >
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl px-5 pt-4 pb-5 max-h-[90vh] overflow-y-auto safe-area-bottom"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-text-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 -mr-1.5 rounded-full text-text-secondary hover:bg-gray-100 touch-active"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="space-y-3">{children}</div>

        {error && (
          <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={saving}
            className="flex-1 rounded-xl bg-[#2563EB] px-4 py-3 text-base font-semibold text-white hover:bg-[#1D4ED8] active:bg-[#1E40AF] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Lightweight confirm dialog (reuses the FormModal overlay/sheet tokens) ───
function ConfirmModal({
  title,
  message,
  confirmLabel,
  destructive,
  busy,
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onCancel}
      style={{ touchAction: 'none' }}
    >
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl px-5 pt-4 pb-5 safe-area-bottom"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-text-primary mb-1.5">{title}</h2>
        <p className="text-sm text-text-secondary leading-snug">{message}</p>
        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={
              destructive
                ? 'flex-1 rounded-xl bg-red-600 px-4 py-3 text-base font-semibold text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-60 flex items-center justify-center gap-2'
                : 'flex-1 rounded-xl bg-[#2563EB] px-4 py-3 text-base font-semibold text-white hover:bg-[#1D4ED8] active:bg-[#1E40AF] disabled:opacity-60 flex items-center justify-center gap-2'
            }
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-text-secondary mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputClass =
  'w-full rounded-xl border border-border-primary bg-white px-3 py-2.5 text-sm text-text-primary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40';

// ── Helpers ──────────────────────────────────────────────────────────────
/** Parse the optional sort-order input: '' → undefined (omit), valid → number, else 'invalid'. */
function parseSortOrder(raw: string): number | undefined | 'invalid' {
  const t = raw.trim();
  if (t === '') return undefined;
  const n = Number(t);
  if (!Number.isInteger(n) || n < 0) return 'invalid';
  return n;
}

/** Answers whose categoryId has no matching category row (defensive). */
function orphanAnswers(
  answers: CannedAnswer[],
  categoryName: Map<number, string>,
): CannedAnswer[] {
  return answers
    .filter((a) => !categoryName.has(a.categoryId))
    .sort((x, y) => x.sortOrder - y.sortOrder || x.id - y.id);
}
